import { BaseCategory } from "./BaseCategory";
import { AssetType, AuthoringBlockType, HeaderPositionType, TextStyleType } from "../constants";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";
import { v4 as uuid } from "uuid";

export class TeamCategory extends BaseCategory {
    get name() {
        return "Team";
    }

    get type() {
        return SlideCategories.TEAM;
    }

    get icon() {
        return null;
    }

    get description() {
        return "Use for introducing multiple team members, leadership, speakers, or key people with their names, titles, and photos. For a single person biography slide, use the Biography category instead.";
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Create a team members slide based on the user's prompt.
        Provide information for each team member including their name and role/title.
        You can optionally provide a search term for their headshot photo.
        Specify how many team members should be shown (between 2 and 8).`;
    }

    getPromptSchema() {
        return {
            teamMembers: [
                {
                    // name: "Full name of the team member",
                    // role: "Job title or role of the team member",
                    text: "richtext with name, role/title and other information about the team member",
                    photoSearch: "optional search term for headshot photo (e.g., 'professional business person', 'executive portrait')"
                }
            ],
            teamMemberCount: "number of team members to include (between 2 and 8)",
            conclusion: "an optional short conclusion or takeaway for the slide",
        };
    }

    getImagePrompt() {
        return false;
    }

    getSlideModels(model, options = {}) {
        const teamMemberCount = Math.min(Math.max(model.teamMemberCount || 3, 2), 8);

        // Ensure we have team members data
        let teamMembers = model.teamMembers || [];

        // If we don't have enough team members, generate placeholder data
        while (teamMembers.length < teamMemberCount) {
            teamMembers.push({
                name: `Team Member ${teamMembers.length + 1}`,
                role: "Role",
                photoSearch: "professional business person headshot"
            });
        }

        // Limit to requested count
        teamMembers = teamMembers.slice(0, teamMemberCount);

        let models = [];

        // Create slide with auto orientation (most flexible)
        models.push(this.buildTeamSlide(model, teamMembers, "auto"));

        // Add vertical orientation variation if 3-6 members
        if (teamMemberCount >= 3 && teamMemberCount <= 6) {
            models.push(this.buildTeamSlide(model, teamMembers, "vertical"));
        }

        // Add horizontal orientation variation if 2-4 members
        if (teamMemberCount >= 2 && teamMemberCount <= 4) {
            models.push(this.buildTeamSlide(model, teamMembers, "horizontal"));
        }

        return models;
    }

    buildTeamSlide(model, teamMembers, orientation = "auto") {
        let builder = new SlideModelBuilder("team", model);

        // Add header if there's a title
        if (model.slideTitle) {
            builder.createHeader({
                heading: model.slideTitle,
                position: HeaderPositionType.TOP
            });
        }

        // Create team member items
        const items = teamMembers.map((member, index) => {
            const item = {
                id: uuid(),
                text: {
                    // blocks: [
                    //     {
                    //         id: uuid(),
                    //         html: member.name || `Team Member ${index + 1}`,
                    //         textStyle: TextStyleType.TITLE,
                    //         type: AuthoringBlockType.TEXT
                    //     },
                    //     {
                    //         id: uuid(),
                    //         html: member.role || "Role",
                    //         textStyle: TextStyleType.BODY,
                    //         type: AuthoringBlockType.TEXT
                    //     }
                    // ]
                    blocks: member.text
                }
            };

            // Add photo if search term is provided
            if (member.photoSearch) {
                Object.assign(item, builder.createImage(this.enhancePhotoSearch(member.photoSearch, member.name), { enableFaceDetection: true }));
                // item.content_type = "image";
                // item.enableFaceDetection = true;
                // item.__generateImage = {
                //     search: this.enhancePhotoSearch(member.photoSearch, member.name),
                //     searchStock: this.enhancePhotoSearch(member.photoSearch, member.name),
                //     assetType: AssetType.IMAGE,
                //     pickIndex: index
                // };
            } else {
                // Placeholder for manual photo upload
                item.content_type = "image";
                item.content_value = "";
            }

            return item;
        });

        // Create the primary team element
        const primaryData = {
            items
        };

        // Set orientation if not auto
        if (orientation !== "auto") {
            primaryData.orientation = orientation;
        }

        builder.createPrimary(primaryData);

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }

        return builder.model;
    }

    enhancePhotoSearch(searchTerm, memberName) {
        // Enhance search term to get better headshots
        // Add context for professional portrait photos
        let enhanced = searchTerm;

        // If the search doesn't already mention professional/portrait context, add it
        if (!searchTerm.match(/professional|portrait|headshot|business/i)) {
            enhanced = `${searchTerm}, professional portrait`;
        }

        // Add face detection friendly terms
        enhanced += ", centered face, well-lit";

        return enhanced;
    }
}
